/**
 * El camino pavimentado para leer datos del servidor en el panel (ADR-0012, paso 3).
 *
 * POR QUÉ EXISTE
 * Convertir pantallas a la caché es mecánico, pero cada conversión obliga a re-derivar las MISMAS
 * cuatro trampas. Después de 26 conversiones a mano quedó claro que el problema no es escribir el
 * bloque: es que **cada repetición es una oportunidad de equivocarse**, y quedan 70 pantallas.
 *
 * Esto NO es una caché hecha a mano —eso es justo lo que el ADR llama parche—. Es una capa fina
 * sobre TanStack Query que **encierra las decisiones que ya se tomaron**, para que lo fácil sea lo
 * correcto. Es lo que hace un equipo de plataforma: no prohibir la herramienta, pavimentar el uso.
 *
 * LAS CUATRO TRAMPAS QUE DEJAN DE PODER OCURRIR
 *
 * 1. 🪤 `isPending` no baja NUNCA con `enabled: false`. Una pantalla sin tenant se quedaba
 *    "cargando" para siempre. Acá `cargando` ya incluye la condición de habilitado.
 *
 * 2. 🪤 `isPending`, jamás `isFetching`. `isFetching` es "estoy revalidando" y usarlo devuelve el
 *    parpadeo que la caché vino a eliminar: mostraría el spinner con datos ya en pantalla.
 *
 * 3. 🪤 v5 quitó `onError` de `useQuery`. Avisar desde el render sería un efecto en medio del
 *    render; acá el aviso vive en un `useEffect` sobre `query.error`, una sola vez y bien.
 *
 * 4. 🪤 `?? []` crea un array NUEVO en cada render e invalida todos los `useMemo` que dependan de
 *    él. Acá la lista vacía es una constante compartida.
 *
 * CUÁNDO **NO** USAR ESTO
 * Cuando la pantalla no encaja, se usa `useQuery` directo y se escribe el porqué. Casos reales:
 *   · Datos que dependen de varias consultas con formas distintas → `useQuery` con un objeto.
 *   · Un error que hay que devolver como DATO y no como fallo (ver `CrmRecompraPage`: si el error
 *     tumba la consulta se pierde el conteo que distingue "sin configurar" de "sin resultados").
 *   · Realtime o updates optimistas que escriben en la caché con `setQueryData`.
 * Forzar la pantalla dentro del molde es peor que salirse de él a conciencia.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData, type QueryKey } from '@tanstack/react-query';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

/** Un minuto. La mayoría de las listas del panel cambian por acción del usuario, no solas. */
const FRESCURA_POR_DEFECTO = 60_000;

/**
 * 🪤 Referencia estable: un `[]` nuevo en cada render invalida los `useMemo` de quien la consuma.
 *
 * Se congela para que un consumidor no pueda mutarla por accidente: como la comparten TODAS las
 * listas vacías del panel, un `push` acá aparecería en pantallas que no tienen nada que ver.
 */
const LISTA_VACIA = Object.freeze([]) as readonly unknown[];

export interface OpcionesCacheadas<T> {
  /**
   * Identidad de la consulta. **Todo parámetro que cambie el resultado va acá** —filtros, página,
   * orden, tenant—. Es lo que hace que volver a la misma vista con los mismos filtros no pida nada.
   */
  clave: QueryKey;
  /**
   * Trae los datos. Debe LANZAR si falla: es lo que dispara el aviso y el reintento.
   *
   * Recibe una `señal` de cancelación que la caché aborta sola cuando la consulta deja de importar
   * —porque cambió un filtro, o porque la pantalla se desmontó—. Pasarla a Supabase
   * (`.abortSignal(señal)`) corta la petición en el aire en vez de esperar una respuesta que ya no
   * se va a usar. Es opcional: la mayoría de las pantallas la ignoran sin consecuencias, porque la
   * caché descarta el resultado viejo igual. Sirve donde la consulta es CARA o el usuario cambia de
   * filtro rápido —un buscador, una tabla paginada—: ahí lo que se ahorra es ancho de banda y una
   * conexión ocupada, no un render de más.
   */
  traer: (señal: AbortSignal) => Promise<T>;
  /** `false` mientras falten datos para consultar (tenant, id de la ruta, sesión cargando). */
  activo?: boolean;
  /** Cuánto vale el dato sin revalidar. Subilo para catálogos, bajalo para cosas que cambian solas. */
  frescura?: number;
  /**
   * Qué decirle al usuario si falla. Sin esto el fallo es MUDO: la pantalla queda vacía y parece
   * que no hay datos, en vez de que no se pudieron traer.
   */
  mensajeDeError?: string;
  /**
   * Para listas paginadas o con filtros: mantiene las filas anteriores mientras llegan las nuevas.
   * Sin esto la tabla se vacía en cada tecleo del buscador y lo que se ve es un parpadeo.
   */
  mantenerAnterior?: boolean;
  /**
   * Milisegundos entre refrescos automáticos, para tableros EN VIVO (sala de call center, colas).
   *
   * ⭐ Mejor que un `setInterval` a mano por dos razones que no son obvias:
   *   · La caché PAUSA el refresco cuando la pestaña está oculta. Un intervalo propio sigue
   *     consultando con la pantalla minimizada — gasto puro, multiplicado por cada pestaña abierta.
   *   · No se encima consigo mismo: si una corrida tarda más que el intervalo, no arranca otra.
   *
   * 🪤 Con esto, bajá también `frescura`: si el dato se considera fresco más tiempo que el
   * intervalo, el refresco se dispara y no trae nada. El reloj corre y la pantalla no se entera.
   */
  refrescoCada?: number;
}

export interface ResultadoCacheado<T> {
  datos: T | undefined;
  /** «No tengo NADA que mostrar todavía» — no «estoy revalidando». */
  cargando: boolean;
  error: Error | null;
  /** Vuelve a pedir aunque el dato esté fresco. Es lo que se llama después de una mutación propia. */
  recargar: () => Promise<void>;
}

/** Versión general: cualquier forma de dato (un objeto, una tupla, lo que sea). */
export function useDatosCacheados<T>({
  clave,
  traer,
  activo = true,
  frescura = FRESCURA_POR_DEFECTO,
  mensajeDeError,
  mantenerAnterior = false,
  refrescoCada,
}: OpcionesCacheadas<T>): ResultadoCacheado<T> {
  const toast = useAdminToastOptional();

  const query = useQuery({
    queryKey: clave,
    enabled: activo,
    staleTime: frescura,
    queryFn: ({ signal }) => traer(signal),
    ...(mantenerAnterior ? { placeholderData: keepPreviousData } : {}),
    // `refetchIntervalInBackground` queda en su default (false): es lo que hace que el refresco se
    // pause con la pestaña oculta, que es la mitad del beneficio.
    ...(refrescoCada ? { refetchInterval: refrescoCada } : {}),
  });

  const error = (query.error as Error | null) ?? null;

  // 🪤 Trampa 3: v5 no tiene `onError`. Acá, una sola vez y en el lugar correcto.
  useEffect(() => {
    if (error && mensajeDeError) toast?.error(mensajeDeError, getErrorMessage(error));
  }, [error, mensajeDeError, toast]);

  const recargar = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    datos: query.data,
    // 🪤 Trampas 1 y 2 juntas: `isPending` (no `isFetching`) Y la condición de habilitado, que sin
    // esto deja el spinner puesto para siempre en una pantalla sin tenant.
    cargando: activo && query.isPending,
    error,
    recargar,
  };
}

export interface ResultadoLista<T> extends Omit<ResultadoCacheado<T[]>, 'datos'> {
  /** Nunca `undefined`: una lista que todavía no llegó es una lista vacía, y así se renderiza igual. */
  datos: T[];
}

/**
 * El caso común del panel: una lista.
 *
 * ```ts
 * const { datos: etiquetas, cargando, recargar } = useListaCacheada<Etiqueta>({
 *   clave: ['wa-tags', tenantId],
 *   activo: !!tenantId,
 *   mensajeDeError: 'No se pudieron cargar las etiquetas',
 *   traer: async () => {
 *     const { data, error } = await supabase.from('wa_tags').select('*').eq('tenant_id', tenantId!);
 *     if (error) throw error;
 *     return (data ?? []) as Etiqueta[];
 *   },
 * });
 * ```
 */
export function useListaCacheada<T>(opciones: OpcionesCacheadas<T[]>): ResultadoLista<T> {
  const r = useDatosCacheados<T[]>(opciones);
  const datos = useMemo(() => r.datos ?? (LISTA_VACIA as unknown as T[]), [r.datos]);
  return { datos, cargando: r.cargando, error: r.error, recargar: r.recargar };
}
